/*
  Christmas countdown. Markup, styles and the first paint:
  snippets/christmas-countdown.liquid. This file keeps the numbers live.

  Each countdown has up to two deadlines:
  - data-order-by: the last order time for Christmas delivery, a fixed
    instant (epoch ms) in the store's time zone, the same for every shopper.
  - Christmas itself: midnight on 25 December in the shopper's own time
    zone, because that is when Christmas starts for them.

  Phases, in order: "order" counts to the last order time; "late" (only
  when a last order time is set) and "christmas" count to Christmas; then
  the countdown removes itself. The Liquid snippet makes the same choice on
  the server. This file repeats it because Shopify caches pages, so a page
  rendered before a deadline can be served after it.

  Minutes, not seconds: a number that changes every second next to the
  price pulls the eye from it, and WCAG 2.2.2 asks for a pause control on
  content that updates that often. Minutes round up, so the clock reads
  "0 min" only when the deadline has passed.

  One shared timer drives every countdown on the page. It wakes when the
  next displayed minute changes and reads the clock each time, so a
  throttled background tab never drifts. It stops while the tab is hidden.
*/
(function () {
  'use strict';

  if (window.ChristmasCountdown) return;

  var MINUTE = 60000;

  var instances = [];
  var timer = null;

  function pad(n) {
    return n < 10 ? '0' + n : String(n);
  }

  // Days, hours and minutes left, minutes rounded up
  function parts(ms) {
    var total = Math.max(0, Math.ceil(ms / MINUTE));
    return {
      days: Math.floor(total / 1440),
      hours: Math.floor((total % 1440) / 60),
      minutes: total % 60
    };
  }

  // Which deadline the clock shows at a given time, or null when all passed
  function phaseAt(now, orderBy, christmas) {
    if (orderBy && now < orderBy) return { name: 'order', target: orderBy };
    if (now < christmas) return { name: orderBy ? 'late' : 'christmas', target: christmas };
    return null;
  }

  function plural(n, one) {
    return n + ' ' + one + (n === 1 ? '' : 's');
  }

  // "66 days, 12 hours and 48 minutes", worded like the Liquid snippet
  function spoken(p) {
    var list = [];
    if (p.days) list.push(plural(p.days, 'day'));
    if (p.days || p.hours) list.push(plural(p.hours, 'hour'));
    list.push(plural(p.minutes, 'minute'));
    if (list.length === 1) return list[0];
    return list.slice(0, -1).join(', ') + ' and ' + list[list.length - 1];
  }

  function Countdown(el) {
    var year = parseInt(el.getAttribute('data-christmas-year'), 10);

    this.el = el;
    this.orderBy = parseInt(el.getAttribute('data-order-by'), 10) || 0;
    this.christmas = new Date(year, 11, 25).getTime();
    this.phase = el.getAttribute('data-phase');
    this.left = 0;

    this.units = {};
    var units = el.querySelectorAll('[data-cc-unit]');
    for (var i = 0; i < units.length; i++) {
      this.units[units[i].getAttribute('data-cc-unit')] = units[i];
    }
    this.copies = el.querySelectorAll('[data-cc-copy]');
    this.a11y = el.querySelector('[data-cc-a11y]');
  }

  Countdown.prototype.setPhase = function (name) {
    if (this.phase === name) return;
    this.phase = name;
    this.el.setAttribute('data-phase', name);
    for (var i = 0; i < this.copies.length; i++) {
      this.copies[i].hidden = this.copies[i].getAttribute('data-cc-copy') !== name;
    }
  };

  // Returns false when the countdown is over and should be dropped
  Countdown.prototype.render = function (now) {
    var phase = phaseAt(now, this.orderBy, this.christmas);
    if (!phase) {
      this.el.hidden = true;
      return false;
    }

    this.setPhase(phase.name);
    this.left = phase.target - now;

    var p = parts(this.left);
    this.write('days', String(p.days));
    this.write('hours', pad(p.hours));
    this.write('minutes', pad(p.minutes));

    // role="timer" is not a live region, so this is read only on request
    if (this.a11y) {
      var text = this.a11y.getAttribute('data-cc-' + phase.name).replace('[time]', spoken(p));
      if (this.a11y.textContent !== text) this.a11y.textContent = text;
    }
    return true;
  };

  Countdown.prototype.write = function (unit, text) {
    var node = this.units[unit];
    if (node && node.textContent !== text) node.textContent = text;
  };

  function tick() {
    var now = Date.now();
    instances = instances.filter(function (instance) {
      return document.contains(instance.el) && instance.render(now);
    });
    schedule();
  }

  // Wake just after the soonest displayed minute changes on any countdown
  function schedule() {
    clearTimeout(timer);
    timer = null;
    if (!instances.length || document.visibilityState === 'hidden') return;
    var wait = MINUTE;
    instances.forEach(function (instance) {
      wait = Math.min(wait, ((instance.left - 1) % MINUTE) + 1);
    });
    timer = setTimeout(tick, Math.max(wait, 0) + 20);
  }

  function init(root) {
    var found = (root || document).querySelectorAll('[data-christmas-countdown]');
    for (var i = 0; i < found.length; i++) {
      if (found[i].christmasCountdown) continue;
      found[i].christmasCountdown = new Countdown(found[i]);
      instances.push(found[i].christmasCountdown);
    }
    tick();
  }

  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') tick();
    else schedule();
  });

  // Back/forward cache restores the page with stale numbers
  window.addEventListener('pageshow', function (evt) {
    if (evt.persisted) tick();
  });

  document.addEventListener('quickview:loaded', function () { init(); });
  document.addEventListener('shopify:section:load', function (evt) { init(evt.target); });

  window.ChristmasCountdown = { init: init, parts: parts, phaseAt: phaseAt, spoken: spoken };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }
})();
